// Perfect Fit — measures how many rendered lines each resume line takes in the
// Word template and computes a strict character budget for the ones that
// overflow. Measurement uses a canvas with the template's font metrics, so it
// works without access to the Office Online preview iframe (cross-origin).
//
// Targets: Career Highlights = 2 lines max, Professional Experience bullets =
// 1 line max. Lines that already fit are never touched.

export interface FitOverflowItem {
  id: string;          // "h3" (highlight index) or "e2.4" (job index . bullet index)
  label: string;       // human label for logs/toasts
  text: string;        // the text Claude may rewrite (highlight DESCRIPTION or full bullet)
  max_chars: number;   // strict budget for the rewritten text
  lines: number;       // measured lines now
  target_lines: number;
}

// Word-template metrics. Usable text column ≈ 6.5in at 96dpi; the templates set
// body text around 10pt. WRAP_EFFICIENCY accounts for width lost to word-wrap
// (lines never pack perfectly); SAFETY shrinks the budget so the rewrite lands
// inside the line even when Word's font rendering differs slightly from canvas.
// Calibrated against the real "Professional 2026 v8" render: a 205-character
// highlight (bold title + text) wraps to 3 lines, which puts the effective
// column at ~470px — the template has wide page margins, an inner content
// inset, and a checkmark/bullet indent. Do NOT widen these without re-checking
// an actual rendered document.
const CONTENT_WIDTH_PX = 560;
const BULLET_INDENT_PX = 40;         // list marker + indent eats into the column
const FONT_REGULAR = '13.3px Calibri, "Segoe UI", Arial, sans-serif'; // 10pt
const FONT_BOLD = 'bold 13.3px Calibri, "Segoe UI", Arial, sans-serif';
const WRAP_EFFICIENCY = 0.90;
const SAFETY = 0.94;

let ctx: CanvasRenderingContext2D | null = null;
function measureCtx(): CanvasRenderingContext2D {
  if (!ctx) ctx = document.createElement("canvas").getContext("2d")!;
  return ctx;
}

function widthOf(text: string, font: string): number {
  const c = measureCtx();
  c.font = font;
  return c.measureText(text).width;
}

/** Estimated rendered line count for a run of (optionally mixed bold/regular) text. */
function estimateLines(totalWidthPx: number, columnPx: number): number {
  const usable = columnPx * WRAP_EFFICIENCY;
  return Math.max(1, Math.ceil(totalWidthPx / usable));
}

/** Character budget so the text lands within targetLines. */
function charBudget(text: string, totalWidthPx: number, columnPx: number, targetLines: number): number {
  const allowed = columnPx * WRAP_EFFICIENCY * targetLines * SAFETY;
  return Math.floor(text.length * (allowed / totalWidthPx));
}

interface HighlightJson { chtitle?: string; chdescription?: string }
interface ExperienceJson { company?: string; Descriptions?: Array<{ Description?: string }> }

/**
 * Scan a flattened Docmosis resume JSON and return every highlight over 2 lines
 * and every experience bullet over 1 line, each with its character budget.
 */
export function findOverflows(flat: {
  CareerHighlights?: HighlightJson[];
  ProfessionalExperience?: ExperienceJson[];
}): FitOverflowItem[] {
  const out: FitOverflowItem[] = [];
  const column = CONTENT_WIDTH_PX - BULLET_INDENT_PX;

  (flat.CareerHighlights ?? []).forEach((h, i) => {
    const title = (h.chtitle ?? "").trim();
    const desc = (h.chdescription ?? "").trim();
    if (!desc) return;
    const titleW = widthOf(`${title}: `, FONT_BOLD);
    const descW = widthOf(desc, FONT_REGULAR);
    const lines = estimateLines(titleW + descW, column);
    if (lines <= 2) return;
    // Budget applies to the description only — the title never changes.
    const totalBudget = charBudget(`${title}: ${desc}`, titleW + descW, column, 2);
    const descBudget = Math.max(60, totalBudget - (title.length + 2));
    out.push({
      id: `h${i}`, label: `Highlight #${i + 1} (${title})`, text: desc,
      max_chars: descBudget, lines, target_lines: 2,
    });
  });

  (flat.ProfessionalExperience ?? []).forEach((j, ji) => {
    (j.Descriptions ?? []).forEach((b, bi) => {
      const t = (b.Description ?? "").trim();
      if (!t) return;
      const w = widthOf(t, FONT_REGULAR);
      const lines = estimateLines(w, column);
      if (lines <= 1) return;
      out.push({
        id: `e${ji}.${bi}`, label: `${j.company ?? "Job"} bullet ${bi + 1}`, text: t,
        max_chars: Math.max(50, charBudget(t, w, column, 1)), lines, target_lines: 1,
      });
    });
  });

  return out;
}

/** Flatten the Claude unified JSON ({clientInfo, documents}) to the flat
 *  Docmosis shape. Passes through data that is already flat. */
export function flattenClaudeUnifiedJson(data: any): any {
  if (!data?.clientInfo || !Array.isArray(data?.documents)) return data;
  const clientInfo = data.clientInfo;
  const resumeDoc = data.documents.find((d: any) => d.documentType === 'resume');
  if (!resumeDoc?.data) return data;
  const [city, state] = (clientInfo.cityST || '').split(',').map((s: string) => s.trim());
  return {
    ...resumeDoc.data,
    Name: resumeDoc.data.Name || clientInfo.fullName,
    Phone: resumeDoc.data.Phone || clientInfo.phoneNumber,
    Email: resumeDoc.data.Email || clientInfo.email,
    City: resumeDoc.data.City || city || clientInfo.cityST || '',
    State: resumeDoc.data.State || state || '',
    LinkedIn: resumeDoc.data.LinkedIn || clientInfo.linkedin,
  };
}

/** Write the trimmed texts back into a DEEP CLONE of the flattened JSON. */
export function applyFits(
  flat: { Summary?: string; CareerHighlights?: HighlightJson[]; ProfessionalExperience?: ExperienceJson[] },
  fits: Array<{ id: string; text: string }>,
): typeof flat {
  const next = structuredClone(flat);
  for (const f of fits) {
    const t = (f.text ?? "").trim();
    if (!t) continue;
    if (f.id === "s") {
      next.Summary = t;
    } else if (f.id.startsWith("h")) {
      const i = Number(f.id.slice(1));
      if (next.CareerHighlights?.[i]) next.CareerHighlights[i].chdescription = t;
    } else if (f.id.startsWith("e")) {
      const [ji, bi] = f.id.slice(1).split(".").map(Number);
      const bullet = jobBullets(next.ProfessionalExperience?.[ji])[bi];
      if (bullet) bullet.Description = t;
    }
  }
  return next;
}


/**
 * A job's bullets in render order. Jobs split into NAMED GROUPS (Named Groups
 * Playbook, Oct 2026) keep their bullets under Groups[].Descriptions with an
 * empty top-level Descriptions; the flat index "e{job}.{k}" counts across the
 * groups in order, the same way the polish pass numbers them.
 */
export function jobBullets(job: any): Array<{ Description?: string }> {
  if (!job) return [];
  const groups = Array.isArray(job.Groups) ? job.Groups : [];
  if (groups.length) return groups.flatMap((g: any) => (Array.isArray(g?.Descriptions) ? g.Descriptions : []));
  return Array.isArray(job.Descriptions) ? job.Descriptions : [];
}
